import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ApiClientError, ApiTransportError } from "@victor/api-client";
import type {
  SuperAdmin,
  SuperAdminLoginInput,
  SuperAdminLoginMfaInput,
  SuperAdminLoginResponse,
} from "@victor/types";
import { api, setAccessToken } from "../lib/api";

type Status = "loading" | "anonymous" | "authenticated";

interface AuthContextValue {
  status: Status;
  superAdmin: SuperAdmin | null;
  login: (input: SuperAdminLoginInput) => Promise<SuperAdminLoginResponse>;
  loginMfa: (
    input: SuperAdminLoginMfaInput,
  ) => Promise<SuperAdminLoginResponse>;
  logout: () => Promise<void>;
  reload: () => Promise<void>;
}

const Ctx = createContext<AuthContextValue | null>(null);

/**
 * Provider d'authentification console.
 *
 * - Access token : en mémoire (jamais localStorage, XSS-safe)
 * - Refresh token : cookie httpOnly géré par le navigateur, path scopé
 *   /v1/super-admin (mutuellement exclusif avec le cookie tenant)
 * - Refresh silencieux au mount, puis programmé 30 s avant expiration
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const [superAdmin, setSuperAdmin] = useState<SuperAdmin | null>(null);
  const refreshTimer = useRef<number | null>(null);

  const scheduleRefresh = useCallback((expiresIn: number) => {
    if (refreshTimer.current) {
      window.clearTimeout(refreshTimer.current);
    }
    const delay = Math.max((expiresIn - 30) * 1000, 5000);
    refreshTimer.current = window.setTimeout(() => {
      void doRefresh();
    }, delay);
  }, []);

  const applyLoginResponse = useCallback(
    (response: SuperAdminLoginResponse) => {
      if ("mfaRequired" in response) {
        // Pas de session ouverte ; le composant Login affichera l'étape MFA.
        return;
      }
      setAccessToken(response.accessToken);
      setSuperAdmin(response.superAdmin);
      setStatus("authenticated");
      scheduleRefresh(response.accessExpiresIn);
    },
    [scheduleRefresh],
  );

  const doRefresh = useCallback(async () => {
    try {
      const response = await api.auth.refresh();
      applyLoginResponse(response);
    } catch (err) {
      // Refresh KO → logout silencieux.
      setAccessToken(null);
      setSuperAdmin(null);
      setStatus("anonymous");
      if (refreshTimer.current) {
        window.clearTimeout(refreshTimer.current);
        refreshTimer.current = null;
      }
      if (
        !(err instanceof ApiClientError) &&
        !(err instanceof ApiTransportError)
      ) {
        console.error("Erreur de refresh inattendue", err);
      }
    }
  }, [applyLoginResponse]);

  // Refresh silencieux au mount.
  useEffect(() => {
    void doRefresh();
    return () => {
      if (refreshTimer.current) {
        window.clearTimeout(refreshTimer.current);
      }
    };
  }, [doRefresh]);

  const login = useCallback(
    async (input: SuperAdminLoginInput) => {
      const response = await api.auth.login(input);
      applyLoginResponse(response);
      return response;
    },
    [applyLoginResponse],
  );

  const loginMfa = useCallback(
    async (input: SuperAdminLoginMfaInput) => {
      const response = await api.auth.loginMfa(input);
      applyLoginResponse(response);
      return response;
    },
    [applyLoginResponse],
  );

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } catch {
      // Best effort : on logout localement de toute façon.
    }
    setAccessToken(null);
    setSuperAdmin(null);
    setStatus("anonymous");
    if (refreshTimer.current) {
      window.clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  }, []);

  const reload = useCallback(async () => {
    try {
      const fresh = await api.auth.me();
      setSuperAdmin(fresh);
    } catch {
      // Si on n'arrive plus à lire /me, on déclenche un refresh pour resync.
      void doRefresh();
    }
  }, [doRefresh]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, superAdmin, login, loginMfa, logout, reload }),
    [status, superAdmin, login, loginMfa, logout, reload],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth hors AuthProvider");
  return ctx;
}
